// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Progress,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { PERFECT_SCROLLBAR_MAX_88 } from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardAiStudio.json";

import "react-table/react-table.css";

function AiStudioDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const experimentsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "ai-studio.col.experiment" }),
        accessor: "name",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "ai-studio.col.model" }),
        accessor: "model",
        Cell: p => <code className="small">{p.value}</code>
      },
      {
        Header: formatMessage({ id: "ai-studio.col.status" }),
        accessor: "status",
        Cell: p => {
          const c =
            p.value === "running"
              ? "success"
              : p.value === "paused"
                ? "warning"
                : "secondary";
          return <Badge color={c}>{p.value}</Badge>;
        }
      },
      {
        Header: formatMessage({ id: "ai-studio.col.traffic" }),
        accessor: "trafficPct",
        Cell: p => `${p.value}%`
      }
    ];
    }, [intl]);

  const promptsColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "ai-studio.col.id" }),
        accessor: "id",
        Cell: p => <code className="small">{p.value}</code>
      },
      {
        Header: formatMessage({ id: "ai-studio.col.preview" }),
        accessor: "preview",
        Cell: p => <span className="text-muted">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "ai-studio.col.model" }),
        accessor: "model",
        Cell: p => <code className="small">{p.value}</code>
      },
      {
        Header: formatMessage({ id: "ai-studio.col.tokens" }),
        accessor: "tokens",
        Cell: p => addCommas(p.value)
      }
    ];
    }, [intl]);

  const modelOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "70%" } },
      colors: [c.ReactifyChartColor1],
      dataLabels: { enabled: true, formatter: v => addCommas(v) },
      xaxis: {
        categories: data.models.map(m => m.name),
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      yaxis: {
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const costOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 2], curve: "smooth" },
      colors: [c.ReactifyChartColor2, c.ReactifyChartColor4],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.costTrend.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: [
        {
          labels: {
            style: { colors: c.primaryColor },
            formatter: v => `$${v}`
          }
        },
        {
          opposite: true,
          labels: {
            style: { colors: c.primaryColor },
            formatter: v => `${v}M`
          }
        }
      ],
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const latencyOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, sparkline: { enabled: false } },
      stroke: { width: 2, curve: "smooth" },
      colors: [c.ReactifyChartColor3],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.latencyByHour.map((_, i) => `${i}h`),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}ms`
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--ai-studio">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.ai-studio" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx md="6" lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-0 small">
                        <IntlMessages id="ai-studio.hero.budget" />
                      </p>
                      <h3 className="mb-0">{k.budgetUsedPct}%</h3>
                    </div>
                    <Button color="primary" size="sm" outline>
                      <IntlMessages id="ai-studio.hero.adjust" />
                    </Button>
                  </div>
                  <Progress value={k.budgetUsedPct} className="mt-2" />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="6" lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3">
                  <p className="text-muted mb-0 small">
                    <IntlMessages id="ai-studio.hero.rpm" />
                  </p>
                  <h3 className="mb-0">{addCommas(k.requestsPerMin)}</h3>
                  <span className="text-success small">+{k.rpmDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx md="12" lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-2 small">
                    <IntlMessages id="ai-studio.section.alerts" />
                  </p>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_88}>
                    <ul className="list-unstyled mb-0 small">
                      {data.alerts.map((a, i) => (
                        <li key={i} className="mb-1">
                          <span className="text-muted me-2">{a.t}</span>
                          {a.msg}
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.tokenSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ai-studio.kpi.tokens" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.tokens24h)}</h4>
                  <span className="text-success small">+{k.tokensDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-bolt text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ai-studio.kpi.latency" />
                  </p>
                  <h4 className="mb-0">{k.avgLatencyMs}ms</h4>
                  <span className="text-success small">{k.latencyDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-exclamation-triangle text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ai-studio.kpi.errors" />
                  </p>
                  <h4 className="mb-0">{k.errorRatePct}%</h4>
                  <span className="text-success small">{k.errorDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="3" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-key text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="ai-studio.kpi.keys" />
                  </p>
                  <h4 className="mb-0">{k.activeKeys}</h4>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ai-studio.chart.models" />
                  </CardTitle>
                  <Chart
                    options={modelOptions}
                    series={[
                      {
                        name: "Requests",
                        data: data.models.map(m => m.requests)
                      }
                    ]}
                    type="bar"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ai-studio.chart.cost" />
                  </CardTitle>
                  <Chart
                    options={costOptions}
                    series={[
                      { name: "Cost (USD)", data: data.costTrend.usd },
                      { name: "Tokens (M)", data: data.costTrend.tokensM }
                    ]}
                    type="line"
                    height={320}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="8" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ai-studio.chart.latency" />
                  </CardTitle>
                  <Chart
                    options={latencyOptions}
                    series={[{ name: "p50", data: data.latencyByHour }]}
                    type="area"
                    height={260}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ai-studio.section.keys" />
                  </CardTitle>
                  <ul className="list-unstyled mb-0">
                    {data.apiKeys.map((key, i) => (
                      <li
                        key={i}
                        className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      >
                        <div>
                          <div className="font-weight-medium">{key.label}</div>
                          <small className="text-muted">{key.lastUsed}</small>
                        </div>
                        <Badge color={key.healthy ? "success" : "warning"}>
                          {key.healthy ? "OK" : "Stale"}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ai-studio.table.experiments" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.experiments}
                    columns={experimentsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="ai-studio.table.prompts" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.recentPrompts}
                    columns={promptsColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default AiStudioDashboard;
